// src/components/ConfirmDialog.jsx
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, DialogContentText } from '@mui/material';

// Predefined dialog types with default texts
const DIALOG_TYPES = {
  delete: {
    title: 'Delete {item}',
    message: 'Are you sure you want to delete this {item}?',
    confirmText: 'Delete',
    confirmColor: 'error'
  },
  submit: {
    title: 'Submit {item}',
    message: 'Are you sure you want to submit this {item}?',
    confirmText: 'Submit',
    confirmColor: 'primary'
  },
  approve: {
    title: 'Approve {item}',
    message: 'Are you sure you want to approve this {item}?',
    confirmText: 'Approve',
    confirmColor: 'success'
  },
  reject: {
    title: 'Reject {item}',
    message: 'Are you sure you want to reject this {item}?',
    confirmText: 'Reject',
    confirmColor: 'error'
  },
  revoke: {
    title: 'Revoke {item}',
    message: 'Are you sure you want to revoke this {item}? The recipient will no longer be able to use it.',
    confirmText: 'Revoke',
    confirmColor: 'error'
  },
  remove: {
    title: 'Remove {item}',
    message: 'Are you sure you want to remove this {item}?',
    confirmText: 'Remove',
    confirmColor: 'error'
  },
  markAsPaid: {
    title: 'Mark as Paid',
    message: 'Are you sure you want to mark this {item} as paid?',
    confirmText: 'Mark as Paid',
    confirmColor: 'success'
  },
  restore: {
    title: 'Restore {item}',
    message: 'Are you sure you want to restore this {item}? This will reactivate their account and they will be able to log in again.',
    confirmText: 'Restore',
    confirmColor: 'primary'
  }
};

// Custom messages for specific item types
const CUSTOM_MESSAGES = {
  approve: {
    user: 'Are you sure you want to approve this user? They will gain access to the platform.',
    'time off request': 'Are you sure you want to approve this time off request?'
  },
  reject: {
    user: 'Are you sure you want to reject this user? They will be notified of the rejection.',
    'time off request': 'Are you sure you want to reject this time off request? The resource will be notified.'
  }
};

/**
 * Reusable confirmation dialog component
 *
 * @example
 * // Using predefined type
 * <ConfirmDialog
 *   open={open}
 *   type="delete"
 *   itemName="invoice"
 *   onConfirm={handleDelete}
 *   onCancel={handleCancel}
 * />
 *
 * @example
 * // Custom text
 * <ConfirmDialog
 *   open={open}
 *   title="Custom Title"
 *   message="Custom message"
 *   confirmText="OK"
 *   onConfirm={handleConfirm}
 *   onCancel={handleCancel}
 * />
 */
const ConfirmDialog = ({
  open,
  type,
  itemName = 'item',
  title,
  message,
  confirmText,
  cancelText = 'Cancel',
  confirmColor,
  onConfirm,
  onCancel
}) => {
  // Get predefined config based on type
  const typeConfig = type ? DIALOG_TYPES[type] : {};

  // Replace {item} placeholder with itemName
  const replacePlaceholder = (text) => {
    return text ? text.replace(/\{item\}/g, itemName) : text;
  };

  // Check for custom message based on type and itemName
  const customMessage = type && itemName && CUSTOM_MESSAGES[type]?.[itemName];

  // Use type config as defaults, allow manual overrides
  const finalTitle = title || replacePlaceholder(typeConfig.title) || 'Confirm Action';
  const finalMessage = message || customMessage || replacePlaceholder(typeConfig.message) || 'Are you sure you want to proceed?';
  const finalConfirmText = confirmText || typeConfig.confirmText || 'Confirm';
  const finalConfirmColor = confirmColor || typeConfig.confirmColor || 'primary';

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{finalTitle}</DialogTitle>
      <DialogContent>
        <DialogContentText>{finalMessage}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="inherit">
          {cancelText}
        </Button>
        <Button onClick={onConfirm} color={finalConfirmColor} variant="contained">
          {finalConfirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  type: PropTypes.oneOf(['delete', 'submit', 'approve', 'reject', 'revoke', 'remove', 'markAsPaid', 'restore']),
  itemName: PropTypes.string,
  title: PropTypes.string,
  message: PropTypes.string,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  confirmColor: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default ConfirmDialog;
